import { addMinutes, format } from "date-fns"

import Icon from "@/components/Icon"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

import { Priority, Wave } from "./types"

interface WaveSlotsProps {
  waves: Wave[]
  priorities: Priority[]
  startTime: string
  interval: number
  onAddWave: () => void
  onRemoveWave: (index: number) => void
  onDriversChange: (index: number, drivers: number) => void
  onSave: () => void
  isPublishing?: boolean
}

const getWaveStartTime = (startTime: string, interval: number, index: number) => {
  const [hours, minutes] = startTime.split(":").map(Number)
  const base = new Date(2000, 0, 1, hours, minutes)
  return format(addMinutes(base, interval * index), "HH:mm")
}

const WaveSlots = ({
  waves,
  priorities,
  startTime,
  interval,
  onAddWave,
  onRemoveWave,
  onDriversChange,
  onSave,
  isPublishing,
}: WaveSlotsProps) => {
  const totalPool = priorities.reduce((sum, p) => sum + p.pool, 0)
  const allocated = Math.min(
    waves.reduce((sum, w) => sum + w.drivers, 0),
    totalPool,
  )
  const remaining = totalPool - allocated

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-palette-jungleGreen900">Wave slots</h3>
        <span className="text-sm text-palette-jungleGreen900">
          Allocated: {allocated} ·{" "}
          <span className={remaining > 0 ? "text-indicators-red" : "text-indicators-green"}>
            {remaining} remaining
          </span>
        </span>
      </div>

      <div className="overflow-auto rounded-lg border border-palette-jungleGreen200">
        <table className="w-full table-fixed">
          <colgroup>
            <col className="w-1/2" />
            <col className="w-1/4" />
            <col className="w-1/4" />
            <col className="w-10" />
          </colgroup>
          <thead className="sticky top-0 z-10">
            <tr className="bg-palette-jungleGreen100">
              <th className="px-4 py-2.5 text-left text-xs font-normal text-palette-jungleGreen900">
                Wave
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-normal text-palette-jungleGreen900">
                Start time
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-normal text-palette-jungleGreen900">
                Drivers
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {waves.map((wave, index) => (
              <tr key={index} className="border-t border-palette-jungleGreen200">
                <td className="px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-palette-jungleGreen900">
                      Wave {index + 1}
                    </p>
                    {wave.distributions.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {wave.distributions.map((dist) => (
                          <Badge
                            key={dist.key}
                            className="border-none bg-palette-honeycomb200 text-xs font-medium text-palette-jungleGreen900 hover:bg-palette-honeycomb200"
                          >
                            {dist.label}: {dist.count}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-palette-jungleGreen900">
                  {getWaveStartTime(startTime, interval, index)}
                </td>
                <td className="px-4 py-3">
                  <Input
                    type="number"
                    min={0}
                    value={wave.drivers === 0 ? "" : wave.drivers}
                    placeholder="0"
                    onChange={(e) => {
                      const val =
                        e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value) || 0)
                      onDriversChange(index, val)
                    }}
                    className="h-9 w-20 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => onRemoveWave(index)}
                    className="text-palette-jungleGreen400 hover:text-palette-jungleGreen700"
                  >
                    <Icon name="bin" className="h-4 w-4 text-palette-jungleGreen900" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={onAddWave}
          className="gap-1 text-xs font-normal"
        >
          <Icon name="plus" className="h-3 w-3 text-white" />
          Add wave
        </Button>
      </div>

      <div className="mt-auto flex justify-end pt-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={onSave}
          disabled={isPublishing}
          className="rounded-full px-10 text-xs font-normal"
        >
          {isPublishing ? "Publishing..." : "Publish"}
        </Button>
      </div>
    </div>
  )
}

export default WaveSlots
